import { Component, OnInit, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { JSONRPCError, Permission, PermissionSubject, PermissionVerb } from '@s8l/client-tree-lib';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import Swal from 'sweetalert2';

import { AlertService } from 'src/app/services/alert.service';
import { CmsService } from 'src/app/services/cms.service';
import { Role, UserServerService } from 'src/app/services/jsonrpc/user-server.service';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-roles',
  templateUrl: './roles.component.html',
  styleUrls: ['./roles.component.scss']
})
export class RolesComponent implements OnInit {
  @ViewChild('usersModal', { static: true }) usersModal;

  public roles: Role[] = [];

  public roleModel = {};
  public roleModelAction: string;
  public userPermission = false;

  public deleteSwalOptions = {
    title: 'Are you sure?',
    text: "You won't be able to revert this!",
    icon: 'question',
    showCancelButton: true,
    confirmButtonText: 'Yes, delete it!',
    buttonsStyling: false,
    customClass: {
      confirmButton: 'btn btn-success',
      cancelButton: 'btn btn-danger'
    }
  };

  public dateFormat = environment.dateFormat;

  public get canEdit(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.Role, PermissionVerb.Write));
  }

  public get canDelete(): boolean {
    return this.cms.hasAccess(new Permission(PermissionSubject.Role, PermissionVerb.Delete));
  }

  private subscription: Subscription;

  constructor(
    private user: UserServerService,
    private modal: NgbModal,
    private toastr: ToastrService,
    private alert: AlertService,
    private users: UserService,
    private cms: CmsService
  ) {}

  public ngOnInit(): void {
    this.userPermission = this.cms.hasAccess(new Permission(PermissionSubject.User, PermissionVerb.Read));
    this.subscription = this.users.roles.subscribe(roles => {
      this.roles = roles;
      if (this.userPermission) {
        this.roles.forEach(r => {
          this.user.roleUsers({ name: r.name }).then(users => ((r as any).users = users.sort()));
        });
      }
    });
  }

  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  public showUsers(r): Promise<any> | void {
    this.roleModel = { ...r };
    const modal = this.modal.open(this.usersModal);
    return modal.result;
  }

  public openCreate(content): Promise<any> {
    this.roleModel = {};
    this.roleModelAction = 'create';
    const modal = this.modal.open(content, { size: 'lg' });
    return modal.result.then(
      res => this.create(res),
      reason => {
        // Throws an exception if reason is not used.
      }
    );
  }

  public openUpdate(content, r): Promise<any> {
    this.roleModel = { ...r };
    this.roleModelAction = 'update';
    const modal = this.modal.open(content, { size: 'lg' });
    return modal.result.then(
      res => this.update(res),
      reason => {
        // Throws an exception if reason is not used.
      }
    );
  }

  public openDelete(name: string): Promise<any> {
    return Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-primary me-1',
        cancelButton: 'btn btn-danger'
      }
    }).then(result => {
      if (result.isConfirmed) {
        this.delete(name);
      }
    });
  }

  public reload(): Promise<any> {
    this.roles = [];
    return this.users.loadRoles();
  }

  public create(role: Role): Promise<any> {
    if (role.description) {
      role.description = role.description.replace(/\s+/g, ' ').trim(); // remove all unnecessary whitespaces
    }
    if (role.name) {
      const newName = role.name.replace(/\s+/g, '').trim(); // remove all whitespaces -> name has to be one word
      if (newName.length == 0) {
        this.toastr.error("Rolename can't be only whitespaces");
        return;
      }
      if (newName != role.name) {
        this.toastr.error('Rolename can only be one word');
        return;
      }
      role.name = newName;
      var existingRole = this.roles.find(oldrole => oldrole.name.toLowerCase() == role.name.toLowerCase());

      if (existingRole) {
        this.alert.fireError('Already exists', `A similar role with the name "${existingRole.name}" already exists.`);
        return;
      }
    }
    return this.users
      .createRole(role)
      .then(() => this.toastr.success(`Role "${role.name}" created`))
      .catch(err => {
        if (err instanceof JSONRPCError && err.code == 3011) {
          return this.alert.fireError('Already exists', `A similar role with the name "${existingRole.name}" already exists.`);
        }
        throw err;
      });
  }

  public update(role: Role): Promise<any> {
    if (role.description) {
      role.description = role.description.replace(/\s+/g, ' ').trim(); // remove all unnecessary whitespaces before updating
    }

    return this.users.updateRole(role).then(() => this.toastr.success(`Role "${role.name}" updated`));
  }

  public delete(role: string): Promise<any> {
    return this.users.deleteRole(role).then(() => this.toastr.success(`Role "${role}" deleted`));
  }
}
